import type { FC } from 'react';
import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';

import { useCaseStudiesLayer } from '../../map-data/CaseStudiesLayerProvider';
import type {
  PoiFilterOptions,
  PoiStudyData,
  StudiesById,
} from '../../map-data/map-data-types';

import { unclusteredPointLayer } from './layers';
import { getRgb } from './get-rgb';
import { ClusterMarkers } from './ClusterMarkers';

interface Props {
  filteredPoiStudyData: PoiStudyData | undefined;
  filteredStudiesById: StudiesById;
  filterOptions: PoiFilterOptions;
}

export const PinMapDataLayers: FC<Props> = (props: Props) => {
  const { filteredPoiStudyData, filteredStudiesById } = props;
  const { showCaseStudiesLayer } = useCaseStudiesLayer();

  const studiesFeatureCollection: GeoJSON.FeatureCollection<GeoJSON.Point> =
    useMemo(() => {
      return {
        type: 'FeatureCollection',
        features: filteredPoiStudyData?.studies.map(item => {
          const design = filteredStudiesById[item.id]?.design;
          const color = getRgb(design);
          return {
            type: 'Feature',
            properties: {
              ...item,
              design, // Explicitly add design for clusterProperties aggregation
              color: `rgb(${color})`,
              border: `rgba(${color}, 0.3)`,
            },
            geometry: {
              type: 'Point',
              coordinates: [item.longitude, item.latitude, 0],
            },
          };
        }) as GeoJSON.Feature<GeoJSON.Point>[],
      };
    }, [filteredPoiStudyData, filteredStudiesById]);

  // Don't render study pins when case studies layer is active
  if (showCaseStudiesLayer) {
    return null;
  }

  return (
    <>
      <Source
        type={'geojson'}
        data={studiesFeatureCollection}
        cluster={true}
        clusterMinPoints={3}
        clusterMaxZoom={50}
        clusterRadius={100}
        clusterProperties={{
          // Aggregate counts for each design type
          clinicalTrialCount: [
            '+',
            ['case', ['==', ['get', 'design'], 'Clinical trial'], 1, 0],
          ],
          implementationStudyCount: [
            '+',
            ['case', ['==', ['get', 'design'], 'Implementation study'], 1, 0],
          ],
          regionalProgrammeCount: [
            '+',
            ['case', ['==', ['get', 'design'], 'Regional programme'], 1, 0],
          ],
          nationalProgrammeCount: [
            '+',
            ['case', ['==', ['get', 'design'], 'National programme'], 1, 0],
          ],
        }}
        id={'data-studies'}
      >
        {/* Only render individual (unclustered) study markers */}
        <Layer {...unclusteredPointLayer} />
      </Source>

      {/* Render custom HTML cluster markers */}
      <ClusterMarkers sourceId="data-studies" />
    </>
  );
};
